// ============================
// File: src/pages/TriagePage.jsx
// ============================
import React, { useEffect, useState } from "react";
import { TriageUI } from "../features/triage";
import DemandPageHeader from "../components/DemandPageHeader";
import DemandSelector from "../components/DemandSelector";
import { useDemandId } from "../context/DemandContext";
import { get } from "../api/http";

export default function TriagePage() {
  // Was: useParams() + a local currentDemandId + manual window.history.pushState.
  // useParams returned {} whenever this page rendered under Dashboard.js rather
  // than a Route, and the pushState calls updated the address bar without telling
  // react-router, so the two navigation systems disagreed about where you were.
  const { demandId, setDemandId } = useDemandId();
  const [resolving, setResolving] = useState(!demandId);
  const [error, setError] = useState("");

  // With nothing selected, land on the first demand awaiting triage.
  useEffect(() => {
    if (demandId) {
      setResolving(false);
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        setResolving(true);
        const result = await get("/demands/list", { query: { limit: 200 } });
        if (cancelled) return;
        const triage = (result?.demands || []).filter((d) =>
          d.current_stage?.toLowerCase().startsWith("triage")
        );
        if (triage.length) setDemandId(triage[0].demand_id);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();

    return () => { cancelled = true; };
  }, [demandId, setDemandId]);

  const header = (
    <DemandPageHeader
      title="Triage"
      description="AI-powered validation and gap analysis for demand screening"
      breadcrumbPath="Triage"
    >
      <DemandSelector currentDemandId={demandId} stage="triage" onDemandChange={setDemandId} />
    </DemandPageHeader>
  );

  if (error) {
    return (
      <div>
        {header}
        <div style={{ margin: 24, padding: 16, border: "1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))",
                      background: "color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))", borderRadius: 8, color: "var(--cares-orange)" }}>
          Could not load demands: {error}
        </div>
      </div>
    );
  }

  if (resolving) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--cares-text-label)" }}>
        Loading Triage demands…
      </div>
    );
  }

  if (!demandId) {
    return (
      <div>
        {header}
        <div style={{ padding: 40, textAlign: "center", color: "var(--cares-text-label)" }}>
          No demands are awaiting triage. Promote an approved idea to create one.
        </div>
      </div>
    );
  }

  return (
    <div>
      {header}
      <TriageUI demandId={demandId} />
    </div>
  );
}
